import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridRowClassNameParams,
  GridRowId,
} from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import PageContainer from '../layout/PageContainer';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_BATCHES_QUERY } from '../../queries/BatchQueries';
import type { Batch } from '../../models/Batch';
import dayjs from 'dayjs';
import { Button, Divider, FormControlLabel, Switch } from "@mui/material";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function BatchList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: Batch[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [includeCompletedBatches, setIncludeCompletedBatches] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<GridRowId>>(new Set<GridRowId>());

  const { loading, error, data } = useQuery(
    GET_BATCHES_QUERY, {
    variables: {
      filter: includeCompletedBatches ? {} : { isComplete: { eq: false } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setRowsState({
      rows: data?.batches || [],
      rowCount: data?.batches.length || 0,
    });
  }, [data?.batches]);

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIncludeCompletedBatches(event.target.checked);

      loadData();
    }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!loading) {
      loadData();
    }
  }, [loading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/batches/${row.id}`);
    },
    [navigate],
  );

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'productName',
        headerName: 'Product',
        width: 200,
        valueGetter: (_, row) => row.product?.name
      },
      {
        field: 'amount',
        headerName: 'Amount (lbs)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'dateTimeCreated',
        headerName: 'Created',
        type: 'dateTime',
        width: 200,
        valueFormatter: (value) => dayjs(value).format('M/D/YYYY h:mm A')
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        width: 200,
        valueGetter: (_, row) => row.product?.customer?.name ?? 'N/A'
      },
      {
        field: 'lotNumber',
        headerName: 'Lot Number',
        width: 200
      },
      {
        field: 'notes',
        headerName: 'Notes',
        width: 200
      },
      {
        field: 'totalCost',
        headerName: 'Total Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (params.row.isComplete) {
      return 'is-complete';
    }

    return '';
  };

  const pageTitle = 'Batches';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <>
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box></Box>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeCompletedBatches}
                      onChange={handleCheckboxFieldChange}
                      size="small"
                      slotProps={{
                        input: { 'aria-label': 'controlled' },
                      }} />}
                  label="Include Completed Batches" />
              </Box>
            </Stack>
            <Box sx={{ height: '58vh', width: '100%' }}>
              <DataGrid
                checkboxSelection
                onRowSelectionModelChange={(newSelectionModel) => {
                  if (newSelectionModel.type === 'exclude') {
                    if (newSelectionModel.ids.size === 0) {
                      setSelectedBatchIds(
                        new Set((rowsState.rows || []).map(batch => batch.id)));
                    } else {
                      const updatedSelectedBatches = rowsState.rows.filter(
                        (row) => !newSelectionModel.ids.has(row.id));

                      setSelectedBatchIds(
                        new Set(updatedSelectedBatches.map(batch => batch.id)));
                    }
                  } else {
                    setSelectedBatchIds(newSelectionModel.ids);
                  }
                }}
                density="compact"
                rows={rowsState.rows}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                disableRowSelectionOnClick
                onRowClick={handleRowClick}
                loading={loading}
                initialState={initialState}
                getRowClassName={getRowClassName}
                showToolbar
                pageSizeOptions={[INITIAL_PAGE_SIZE]}
                sx={{
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  },
                  '.is-complete': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      '#4caf50' :
                      '#4caf50',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                        '#00e676' :
                        '#00e676',
                      filter: 'brightness(1.1)'
                    }
                  }
                }}
                slotProps={{
                  loadingOverlay: {
                    variant: 'circular-progress',
                    noRowsVariant: 'circular-progress',
                  },
                  baseIconButton: {
                    size: 'small',
                  },
                }}
              />
            </Box>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Box></Box>
              <Button
                variant="contained"
                disabled={(selectedBatchIds || []).size === 0}
                onClick={() => navigate(`/dashboard/batches/process?batchIds=${[...(selectedBatchIds || [])].join(',')}`)}
              >
                See The Maths
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </PageContainer>
  );
}